import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalEmbarkStyles, PageWrap, Topbar, ReEmbarkBtn, Body,
  MainCol, ScanCard, ScanIcon, ScanTitle, ScanHint, ScanInput, ScanFeedback,
  ResultCard,
  SideCol, SideHeader, SessionStats, SessionStat,
  HistoryList, HistoryEmpty, HistoryItem,
  ModalOverlay, ReBox, StepIndicator, Step, StepArrow,
  ReInput, SuggestionBox, InfoLine,
  ModalActions, ModalBtn,
  BoxBanner, VolumesPanel, VolumesList, VolumeRow,
  BulkEmbarkBtn, ResetBoxBtn, RedirectBanner, EmptyBoxState,
} from './style'
import FeedbackModal, { FeedbackExtra } from '../_shared/FeedbackModal'
import { useAudioUnlock, playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { useScannerInput, useIsCoarsePointer, HIDDEN_INPUT_STYLE } from '../_shared/scanner'
import { normalizeRastreio } from '../_shared/rastreio'
import { printRomaneioByDriver } from '../_shared/printRomaneio'
import { formatBeep } from '../_shared/datetime'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function extractNfFromBarcode(raw) {
  const beforeV = String(raw).match(/(\d+)(?=V\d)/i)
  if (beforeV) return beforeV[1]
  const onlyDigits = String(raw).match(/\d+/)
  return onlyDigits ? onlyDigits[0] : null
}
function extractVolumeFromBarcode(raw) {
  const m = String(raw).match(/V(\d+)\s*$/i)
  return m ? parseInt(m[1], 10) : 1
}
function fmtTime(d) {
  if (!(d instanceof Date)) d = new Date(d)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
function cleanRecipientName(raw) {
  return (raw || '').replace(/\s*-\s*\d{11}\s*$/, '').trim()
}
// Data de hoje no horário LOCAL (sem toISOString, que converteria pra UTC).
function todayLocalISO() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogEmbark(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Estado do scanner principal
  const [scanValue, setScanValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState({ kind: null, message: '' })
  const [lastResult, setLastResult] = useState(null)
  const scanInputRef = useRef(null)
  const refocusTimerRef = useRef(null)
  const completeResetTimerRef = useRef(null)

  // Box atualmente carregado + volumes pendentes / embarcados
  const [currentBox, setCurrentBox] = useState(null) // { id, name, barcode, driver_name?, total }
  const [boxVolumes, setBoxVolumes] = useState([])   // [{ id, rastreio, volume, status, order: {...} }]
  const [redirectBox, setRedirectBox] = useState(null) // { id, name, barcode } quando rastreio é de outro box

  // Histórico da sessão
  const [historyList, setHistoryList] = useState([])
  const ok_count = historyList.filter(h => h.kind === 'ok').length
  const err_count = historyList.length - ok_count

  // Modal re-embarcar (legado — mantido funcional)
  const [reModal, setReModal] = useState({
    open: false, step: 1, scanNf: '', scanBox: '',
    order: null, suggestion: null, volumeNumber: 1,
    feedback: { kind: null, message: '' }, busy: false,
  })
  const reNfInputRef = useRef(null)
  const reBoxInputRef = useRef(null)

  // Modal de feedback (error / warning / success) + áudio compartilhado
  useAudioUnlock()
  const initialFbModal = {
    open: false, kind: 'error', title: '', message: '',
    correct_box: null,
    pending_siblings_count: null,
    incomplete_orders: null,
    print: null,            // { driver_id, driver_name, date } → habilita "Imprimir Romaneio"
    reset_on_close: false,  // ao fechar o modal, limpa o box (usado nas conclusões)
  }
  const [fbModal, setFbModal] = useState(initialFbModal)
  const fbModalOpenRef = useRef(false)
  useEffect(() => { fbModalOpenRef.current = fbModal.open }, [fbModal.open])
  // Flag (em ref) pra closeFbModal saber se deve resetar o box ao fechar
  const fbResetRef = useRef(false)
  useEffect(() => { fbResetRef.current = !!fbModal.reset_on_close }, [fbModal.reset_on_close])

  // Scanner: arma o input ativo conforme o estado (principal ou modal de reentrega).
  // armKey combina os estados que trocam o input ativo:
  //   - reModal aberto/fechado e step do modal
  //   - currentBox: na tela principal, bipa "box" antes; depois bipa "rastreio"
  const isCoarse = useIsCoarsePointer()
  const { arm: armScanner } = useScannerInput({
    getInput: () => {
      if (reModal.open) {
        return reModal.step === 1 ? reNfInputRef.current : reBoxInputRef.current
      }
      return scanInputRef.current
    },
    getDisabled: () => auth !== 'authorized' || fbModal.open,
    armKey: `${reModal.open ? 're:' + reModal.step : 'main:' + (currentBox?.id || 'no-box')}`,
  })

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth === 'authorized') setTimeout(armScanner, 100)
  }, [auth, armScanner])

  // Re-foca o input após cada operação (skip se modal aberto)
  useEffect(() => {
    if (auth !== 'authorized' || reModal.open || fbModal.open) return
    clearTimeout(refocusTimerRef.current)
    refocusTimerRef.current = setTimeout(armScanner, 100)
    return () => clearTimeout(refocusTimerRef.current)
  }, [busy, reModal.open, fbModal.open, auth, armScanner])

  function closeFbModal() {
    if (fbResetRef.current) resetBox()
    setFbModal(initialFbModal)
    setTimeout(armScanner, 30)
  }

  // Imprime o romaneio do motorista do box recém-embarcado (a partir do modal).
  async function handlePrintRomaneio() {
    const info = fbModal.print
    if (!info) return
    try {
      const { ok, error } = await printRomaneioByDriver({
        driverId: info.driver_id,
        date: info.date,
      })
      if (!ok && error === 'popup_blocked') {
        toast.warn('Permita pop-ups neste site para imprimir o romaneio.')
      }
    } catch (err) {
      console.error('print romaneio failed:', err)
      toast.error('Erro ao gerar romaneio: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    }
  }

  // Foco perpétuo: se perder o foco, volta pro input principal em 50ms
  const handleEmbarkBlur = useCallback(() => {
    setTimeout(() => {
      if (fbModalOpenRef.current) return
      if (reModal.open) return
      const ae = document.activeElement
      if (ae && ae !== document.body) {
        const tag = ae.tagName
        if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      }
      armScanner()
    }, 50)
  }, [reModal.open, armScanner])

  // ── HISTORY ────────────────────────────────────────────────────────────────
  const pushHistory = useCallback((entry) => {
    setHistoryList(prev => [{ ...entry, at: new Date() }, ...prev].slice(0, 50))
  }, [])

  // ── BOX-FIRST FLOW ─────────────────────────────────────────────────────────
  /**
   * Bipa o barcode do box → GET /box/by-barcode → GET /box/:id/embark-list
   * Mostra o box ativo + lista de volumes que ainda precisam ser embarcados.
   */
  const loadBoxByBarcode = useCallback(async (barcode) => {
    setBusy(true)
    setFeedback({ kind: null, message: 'Buscando box…' })
    try {
      const boxRes = await apiSantoLog.get(`/box/by-barcode/${encodeURIComponent(barcode)}`, { params: { module: 'embarque' } }).catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!boxRes) {
        playErrorBeep()
        setFbModal({ open: true, kind: 'error', title: 'Box não encontrado', message: `Nenhum box com código "${barcode}" foi localizado.` })
        setFeedback({ kind: 'err', message: `Box "${barcode}" não encontrado` })
        return
      }
      const box = boxRes.data?.box || boxRes.data || {}

      // Lista de volumes em no_box deste box
      const listRes = await apiSantoLog.get(`/box/${box.id}/embark-list`)
      const data = listRes.data || {}
      const boxInfo = data.box || box
      const volumes = data.volumes || []

      setCurrentBox({
        id: boxInfo.id,
        name: boxInfo.name || box.name,
        barcode: boxInfo.barcode || box.barcode,
        driver_name: boxInfo.driver?.name || boxInfo.driver_name || null,
        driver_id: boxInfo.driver?.id || boxInfo.driver_id || null,
        total: data.total || volumes.length,
      })
      setBoxVolumes(volumes)
      setRedirectBox(null)
      setLastResult(null)

      const total = data.total || volumes.length
      playSuccessBeep()
      if (total === 0) {
        setFeedback({ kind: 'ok', message: `📦 ${boxInfo.name || box.name} está vazio (sem volumes pra embarcar)` })
      } else {
        setFeedback({
          kind: 'ok',
          message: `📦 ${boxInfo.name || box.name} · ${total} volume${total !== 1 ? 's' : ''} pra embarcar`,
        })
      }
    } catch (err) {
      console.error('GET embark-list failed:', err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      playErrorBeep()
      setFbModal({ open: true, kind: 'error', title: 'Erro', message: 'Erro: ' + msg })
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
    } finally {
      setBusy(false)
    }
  }, [])

  /**
   * Bipa um rastreio → POST /volume/embark { box_id, rastreio }.
   * Trata: success / already_embarked / 422 com actual_box.
   */
  const embarkRastreio = useCallback(async (rastreio) => {
    if (!currentBox) return
    setBusy(true)
    setFeedback({ kind: null, message: 'Embarcando…' })
    setRedirectBox(null)
    try {
      const res = await apiSantoLog.post('/volume/embark', {
        box_id: currentBox.id,
        rastreio,
      })
      const data = res.data || {}
      const volume = data.volume || {}
      const orderStatus = data.order_status

      if (data.already_embarked) {
        playWarningBeep()
        setFeedback({ kind: null, message: `⚠ ${rastreio} já estava embarcado` })
        setFbModal({
          open: true, kind: 'warning',
          title: 'Volume já embarcado',
          message: `O volume ${rastreio} já estava embarcado. Nenhuma ação tomada.`,
        })
      } else {
        playSuccessBeep()
        setFeedback({
          kind: 'ok',
          message: `✓ ${rastreio} embarcado${orderStatus === 'embarcado' ? ' · pedido completo!' : ''}`,
        })
        pushHistory({
          kind: 'ok',
          order_number: extractNfFromBarcode(rastreio) || '—',
          recipient_name: '—',
          message: `${currentBox.name} · ${orderStatus === 'embarcado' ? 'pedido completo' : 'parcial'}`,
        })
      }

      // Marca o volume como embarcado na lista local (não remove — mostra riscado)
      setBoxVolumes(prev => prev.map(v => {
        const matches = v.rastreio === rastreio || v.id === volume.id
        return matches ? { ...v, status: 'embarcado' } : v
      }))

      setLastResult({ kind: 'ok', rastreio, order_status: orderStatus })

      // Se TODOS embarcados, abre o modal de conclusão (com opção de imprimir romaneio).
      // O box é resetado quando o operador fecha o modal (reset_on_close).
      const remaining = boxVolumes.filter(v => v.status !== 'embarcado' && v.rastreio !== rastreio).length
      if (remaining === 0) {
        clearTimeout(completeResetTimerRef.current)
        setFeedback({ kind: 'ok', message: '✓ Box completo!' })
        playSuccessBeep()
        setFbModal({
          open: true, kind: 'success',
          title: 'Box embarcado',
          message: `Todos os volumes do ${currentBox.name} foram embarcados.`,
          print: {
            driver_id: currentBox.driver_id || null,
            driver_name: currentBox.driver_name || null,
            date: todayLocalISO(),
          },
          reset_on_close: true,
        })
      }
    } catch (err) {
      console.error('POST /volume/embark failed:', err)
      const data = err?.response?.data || {}
      const status = err?.response?.status

      // 422 com actual_box → warning (regra de negócio: volume em outro box)
      if (status === 422 && data.actual_box) {
        playWarningBeep()
        setRedirectBox(data.actual_box)
        setFbModal({
          open: true, kind: 'warning',
          title: 'Volume não pertence a este box',
          message: data.error || 'Volume está em outro box',
          correct_box: data.actual_box,
        })
        setFeedback({ kind: 'err', message: data.error || `Volume está em outro box` })
        pushHistory({
          kind: 'err',
          order_number: extractNfFromBarcode(rastreio) || '—',
          recipient_name: '—',
          message: `Rastreio do ${data.actual_box.name}`,
        })
        return
      }

      // 422 com pending_siblings_count → pedido incompleto, não pode embarcar.
      // Modal bloqueante (operador precisa fechar pra continuar).
      if (status === 422 && data.pending_siblings_count != null) {
        playWarningBeep()
        const pending = data.pending_siblings_count
        setFbModal({
          open: true, kind: 'warning',
          title: 'Pedido incompleto — não embarcou',
          message: data.error || `O pedido tem ${pending} volume${pending !== 1 ? 's' : ''} ainda pendente${pending !== 1 ? 's' : ''} de coleta. Conclua a separação antes de embarcar.`,
          pending_siblings_count: pending,
        })
        setFeedback({ kind: 'err', message: `Pedido com ${pending} volume(s) pendente(s)` })
        pushHistory({
          kind: 'err',
          order_number: extractNfFromBarcode(rastreio) || rastreio,
          recipient_name: '—',
          message: `${pending} volume(s) pendente(s) de coleta`,
        })
        return
      }

      const msg = data.error || data.message || err.message
      if (status === 404) {
        playErrorBeep()
        setFbModal({ open: true, kind: 'error', title: 'Não encontrado', message: msg })
      } else if (status === 422) {
        playWarningBeep()
        setFbModal({ open: true, kind: 'warning', title: 'Atenção', message: msg })
      } else {
        playErrorBeep()
        setFbModal({ open: true, kind: 'error', title: 'Erro', message: 'Erro: ' + msg })
      }
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
      pushHistory({
        kind: 'err',
        order_number: extractNfFromBarcode(rastreio) || rastreio,
        recipient_name: '—',
        message: msg,
      })
    } finally {
      setBusy(false)
    }
  }, [currentBox, boxVolumes, pushHistory]) // eslint-disable-line

  /**
   * Embarca em massa todos os volumes restantes — POST /box/embark { box_id }.
   */
  const embarkAll = useCallback(async () => {
    if (!currentBox) return
    const remaining = boxVolumes.filter(v => v.status !== 'embarcado')
    if (remaining.length === 0) return
    if (!window.confirm(`Embarcar todos os ${remaining.length} volumes restantes do ${currentBox.name}?`)) return
    setBusy(true)
    setFeedback({ kind: null, message: 'Embarcando em massa…' })
    try {
      const res = await apiSantoLog.post('/box/embark', { box_id: currentBox.id })
      const data = res.data || {}
      const embarked = data.embarked_count || 0
      const affected = data.affected_order_ids || []
      const skipped = data.skipped || {}
      const skippedVolumeIds = Array.isArray(skipped.volume_ids) ? skipped.volume_ids : []
      const incompleteOrders = Array.isArray(skipped.incomplete_orders) ? skipped.incomplete_orders : []
      const hasSkipped = incompleteOrders.length > 0

      // Marca como embarcado SÓ os volumes que não foram pulados pelo backend
      const skippedSet = new Set(skippedVolumeIds)
      setBoxVolumes(prev => prev.map(v => (
        skippedSet.has(v.id) ? v : { ...v, status: 'embarcado' }
      )))

      // Modal: success se tudo embarcou; warning se algum pedido foi pulado
      if (hasSkipped) {
        playWarningBeep()
        setFbModal({
          open: true, kind: 'warning',
          title: 'Embarque parcial — alguns pedidos foram pulados',
          message: `${embarked} volume${embarked !== 1 ? 's' : ''} embarcado${embarked !== 1 ? 's' : ''}, mas ${incompleteOrders.length} pedido${incompleteOrders.length !== 1 ? 's' : ''} não foi embarcado por ter volumes pendentes de coleta.`,
          incomplete_orders: incompleteOrders,
        })
        setFeedback({
          kind: 'warn',
          message: `⚠ ${embarked} embarcado(s) · ${incompleteOrders.length} pedido(s) pulado(s)`,
        })
      } else {
        playSuccessBeep()
        setFbModal({
          open: true, kind: 'success',
          title: 'Embarque em massa concluído',
          message: `${embarked} volume${embarked !== 1 ? 's' : ''} embarcado${embarked !== 1 ? 's' : ''} · ${affected.length} pedido${affected.length !== 1 ? 's' : ''} afetado${affected.length !== 1 ? 's' : ''}`,
          print: {
            driver_id: currentBox.driver_id || null,
            driver_name: currentBox.driver_name || null,
            date: todayLocalISO(),
          },
          reset_on_close: true,
        })
        setFeedback({
          kind: 'ok',
          message: `✓ ${embarked} volume${embarked !== 1 ? 's' : ''} embarcado${embarked !== 1 ? 's' : ''}`,
        })
      }

      pushHistory({
        kind: hasSkipped ? 'err' : 'ok',
        order_number: '—',
        recipient_name: `${currentBox.name} (massa)`,
        message: hasSkipped
          ? `${embarked} embarcado(s) · ${incompleteOrders.length} pulado(s)`
          : `${embarked} volumes · ${affected.length} pedidos`,
      })

      // O reset agora acontece quando o operador fecha o modal:
      // - sucesso → modal com reset_on_close (e opção de imprimir romaneio)
      // - com pulados → operador lê o aviso e fecha manualmente
      clearTimeout(completeResetTimerRef.current)
    } catch (err) {
      console.error('POST /box/embark failed:', err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      playErrorBeep()
      setFbModal({ open: true, kind: 'error', title: 'Erro no embarque em massa', message: 'Erro: ' + msg })
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
    } finally {
      setBusy(false)
    }
  }, [currentBox, boxVolumes, pushHistory])

  function resetBox() {
    clearTimeout(completeResetTimerRef.current)
    setCurrentBox(null)
    setBoxVolumes([])
    setRedirectBox(null)
    setLastResult(null)
    setFeedback({ kind: null, message: '' })
  }

  function switchToRedirectBox() {
    if (!redirectBox) return
    loadBoxByBarcode(redirectBox.barcode)
  }

  // Dispatcher do input principal: decide se é box ou rastreio baseado no estado.
  // Rastreio bipado é normalizado (SLEXPRESSO, TMRAPIDO etc → ALEF{NF}V{VOL}).
  // O código do BOX não passa por normalização — é o barcode literal.
  function onMainScanEnter(code) {
    if (!code) return
    if (!currentBox) {
      loadBoxByBarcode(code)
    } else {
      embarkRastreio(normalizeRastreio(code))
    }
  }

  function onScanKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const code = scanValue.trim()
      setScanValue('')
      onMainScanEnter(code)
    }
    if (e.key === 'Escape') setScanValue('')
  }

  // ── MODAL RE-EMBARCAR (legado, mantido) ────────────────────────────────────
  function openReEmbark() {
    setReModal({
      open: true, step: 1, scanNf: '', scanBox: '',
      order: null, suggestion: null, volumeNumber: 1,
      feedback: { kind: null, message: '' }, busy: false,
    })
    setTimeout(armScanner, 80)
  }
  function closeReEmbark() { setReModal(m => ({ ...m, open: false })) }

  async function reStep1Submit() {
    const code = reModal.scanNf.trim()
    if (!code) return
    const nf = extractNfFromBarcode(code)
    const volNum = extractVolumeFromBarcode(code)
    if (!nf) {
      setReModal(m => ({ ...m, feedback: { kind: 'err', message: 'Código inválido' } }))
      return
    }
    setReModal(m => ({ ...m, busy: true, feedback: { kind: null, message: 'Buscando pedido…' } }))
    try {
      const lookup = await apiSantoLog.get(`/order/by-number/${nf}`, { params: { module: 'embarque' } }).catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!lookup) {
        setReModal(m => ({ ...m, busy: false, feedback: { kind: 'err', message: `NF ${nf} não cadastrada` } }))
        return
      }
      const order = lookup.data?.order || lookup.data || {}
      if (order.status !== 'embarcado') {
        const msg = `Pedido ${nf} está em "${order.status}" — não pode ser re-embarcado`
        setReModal(m => ({ ...m, busy: false, feedback: { kind: 'err', message: msg } }))
        return
      }

      // Reverte status → coletado e busca sugestão de box
      await apiSantoLog.put(`/order/by-number/${nf}/status`, { status: 'coletado' })
      const suggestRes = await apiSantoLog.get('/box/suggest', {
        params: { order_id: order.id, driver_id: order.driver_id || undefined },
      })
      const suggestion = suggestRes.data?.suggestion || null
      setReModal(m => ({
        ...m, busy: false, step: 2, order, volumeNumber: volNum, suggestion, scanBox: '',
        feedback: { kind: 'ok', message: '✓ Status revertido. Bipe o box pra registrar.' },
      }))
      setTimeout(armScanner, 80)
    } catch (err) {
      console.error('re-embark step1 failed:', err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      setReModal(m => ({ ...m, busy: false, feedback: { kind: 'err', message: 'Erro: ' + msg } }))
    }
  }

  async function reStep2Submit() {
    const code = reModal.scanBox.trim()
    if (!code) return
    setReModal(m => ({ ...m, busy: true, feedback: { kind: null, message: 'Buscando box…' } }))
    try {
      const boxRes = await apiSantoLog.get(`/box/by-barcode/${encodeURIComponent(code)}`, { params: { module: 'embarque' } }).catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!boxRes) {
        setReModal(m => ({ ...m, busy: false, feedback: { kind: 'err', message: `Box "${code}" não encontrado` } }))
        return
      }
      const box = boxRes.data?.box || boxRes.data || {}
      await apiSantoLog.post('/box/scan', {
        box_id: box.id,
        order_id: reModal.order.id,
        volume_number: reModal.volumeNumber,
      })
      toast.success(`✓ NF ${reModal.order.order_number} → Box ${box.name}`)
      pushHistory({
        kind: 'ok',
        order_number: reModal.order.order_number,
        recipient_name: reModal.order.recipient_name || '—',
        message: `Re-embarcado em ${box.name}`,
      })
      closeReEmbark()
    } catch (err) {
      console.error('re-embark step2 failed:', err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      setReModal(m => ({ ...m, busy: false, feedback: { kind: 'err', message: 'Erro: ' + msg } }))
    }
  }

  // ── DERIVED ────────────────────────────────────────────────────────────────
  const embarkedCount = boxVolumes.filter(v => v.status === 'embarcado').length
  const totalCount = boxVolumes.length
  const remainingCount = totalCount - embarkedCount
  const allEmbarked = totalCount > 0 && remainingCount === 0

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Embarcar">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalEmbarkStyles />
          <PageWrap>
            <Topbar>
              <h2>🚛 Embarque</h2>
              <span className="crumb">
                {!currentBox
                  ? 'Bipe o box pra começar'
                  : `${currentBox.name} · ${embarkedCount}/${totalCount} embarcados`}
              </span>
              <ReEmbarkBtn onClick={openReEmbark}>↩ Re-embarcar</ReEmbarkBtn>
            </Topbar>

            <Body>
              <MainCol>
                {/* Card de scan — texto/placeholder muda conforme o estado */}
                <ScanCard>
                  <ScanIcon>{!currentBox ? '📦' : '📡'}</ScanIcon>
                  <ScanTitle>
                    {!currentBox ? 'Bipar box pra iniciar embarque' : 'Bipar volume pra embarcar'}
                  </ScanTitle>
                  <ScanHint>
                    {!currentBox
                      ? <>Comece bipando o <strong style={{ color: '#00e5a0' }}>código do box</strong>. Vou listar os volumes prontos pra embarcar.</>
                      : <>Bipe cada rastreio em <strong style={{ color: '#00e5a0' }}>{currentBox.name}</strong>. Ou clique em "Embarcar todos" pra embarcar em massa.</>}
                  </ScanHint>
                  <ScanInput
                    ref={scanInputRef}
                    value={scanValue}
                    onChange={e => setScanValue(e.target.value)}
                    onKeyDown={onScanKeyDown}
                    onBlur={handleEmbarkBlur}
                    placeholder={!currentBox ? 'Bipe o código do box' : 'Bipe o rastreio do volume'}
                    disabled={busy}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                  />
                  <ScanFeedback $kind={feedback.kind}>
                    {feedback.message || (busy ? 'Processando…' : 'Aguardando bipe…')}
                  </ScanFeedback>
                </ScanCard>

                {/* Banner do box atual */}
                {currentBox && (
                  <BoxBanner $complete={allEmbarked}>
                    <div className="info">
                      <div className="lbl">Box atual</div>
                      <div className="name">📦 {currentBox.name}</div>
                      <div className="meta">
                        {currentBox.barcode && <>código <strong>{currentBox.barcode}</strong></>}
                        {currentBox.driver_name && <> · motorista <strong>{currentBox.driver_name}</strong></>}
                      </div>
                    </div>
                    <div>
                      <div className="progress">{embarkedCount}/{totalCount}</div>
                      <div className="progress-lbl">Embarcados</div>
                    </div>
                  </BoxBanner>
                )}

                {/* Redirect banner quando o rastreio é de outro box */}
                {redirectBox && (
                  <RedirectBanner>
                    <span className="icon">↪</span>
                    <span className="text">
                      Esse volume pertence ao <strong>{redirectBox.name}</strong>.
                    </span>
                    <button type="button" onClick={switchToRedirectBox}>
                      Ir pro {redirectBox.name} →
                    </button>
                  </RedirectBanner>
                )}

                {/* Lista de volumes do box atual */}
                {currentBox && totalCount > 0 && (
                  <VolumesPanel>
                    <h4>Volumes ({embarkedCount}/{totalCount} embarcados)</h4>
                    <VolumesList>
                      {boxVolumes.map(v => {
                        const embarked = v.status === 'embarcado'
                        const order = v.order || {}
                        return (
                          <VolumeRow key={v.id || v.rastreio} $embarked={embarked}>
                            <span className="check">{embarked ? '✓' : '○'}</span>
                            <div className="body">
                              <div className="rastreio">{v.rastreio || '—'}</div>
                              <div className="meta">
                                NF {order.order_number || '—'}
                                {order.recipient_name && ` · ${cleanRecipientName(order.recipient_name)}`}
                                {order.sequence_order != null && ` · seq ${order.sequence_order}`}
                                {order.driver_name && ` · 🚛 ${order.driver_name}`}
                                {currentBox?.name && ` · 📦 ${currentBox.name}`}
                                {v.first_scanned_at && ` · 🔵 1º beep ${formatBeep(v.first_scanned_at)}`}
                              </div>
                            </div>
                            <span className="nf">V{v.volume || v.volume_number || '?'}</span>
                          </VolumeRow>
                        )
                      })}
                    </VolumesList>
                  </VolumesPanel>
                )}

                {/* Box vazio (sem volumes pra embarcar) */}
                {currentBox && totalCount === 0 && (
                  <EmptyBoxState>
                    <div className="icon">📦</div>
                    <h4>{currentBox.name} está vazio</h4>
                    <p>Nenhum volume em no_box. Bipe outro box pra continuar.</p>
                  </EmptyBoxState>
                )}

                {/* Botões de ação quando box carregado */}
                {currentBox && (
                  <>
                    {remainingCount > 0 && (
                      <BulkEmbarkBtn onClick={embarkAll} disabled={busy}>
                        🚛 Embarcar todos os {remainingCount} restantes
                      </BulkEmbarkBtn>
                    )}
                    <ResetBoxBtn onClick={resetBox} disabled={busy}>
                      ✕ Trocar de box
                    </ResetBoxBtn>
                  </>
                )}

                {/* Result card do último volume embarcado */}
                {lastResult && lastResult.kind === 'ok' && !currentBox && (
                  <ResultCard $kind="ok">
                    <span className="icon">✅</span>
                    <div className="info">
                      <div className="nf">{lastResult.rastreio}</div>
                      <div className="msg">Embarcado · status: {lastResult.order_status}</div>
                    </div>
                  </ResultCard>
                )}
              </MainCol>

              {/* COL DIREITA: histórico da sessão */}
              <SideCol>
                <SideHeader>
                  <h3>Sessão atual</h3>
                  <p>Histórico dos últimos bipes (volátil — perde ao recarregar)</p>
                </SideHeader>
                <SessionStats>
                  <SessionStat $variant="ok">
                    <div className="l">Embarcados</div>
                    <div className="v">{ok_count}</div>
                  </SessionStat>
                  <SessionStat $variant="err">
                    <div className="l">Falhas</div>
                    <div className="v">{err_count}</div>
                  </SessionStat>
                </SessionStats>
                <HistoryList>
                  {historyList.length === 0 ? (
                    <HistoryEmpty>
                      Nenhum bipe registrado ainda.<br />
                      Comece bipando um box.
                    </HistoryEmpty>
                  ) : (
                    historyList.map((h, i) => (
                      <HistoryItem key={i} $kind={h.kind}>
                        <span className="icon">{h.kind === 'ok' ? '✓' : '✗'}</span>
                        <div className="info">
                          <div className="nf">NF {h.order_number}</div>
                          <div className="name">{h.recipient_name} · {h.message}</div>
                        </div>
                        <span className="time">{fmtTime(h.at)}</span>
                      </HistoryItem>
                    ))
                  )}
                </HistoryList>
              </SideCol>
            </Body>
          </PageWrap>

          {/* MODAL RE-EMBARCAR */}
          <ModalOverlay $visible={reModal.open}>
            <ReBox>
              <h3>↩ Re-embarcar pedido</h3>
              <p>
                Reverte um pedido <strong>embarcado</strong> pra <strong>coletado</strong> e o coloca novamente em um box.
                Útil quando o pedido foi marcado por engano.
              </p>

              <StepIndicator>
                <Step $active={reModal.step === 1} $done={reModal.step > 1}>
                  <span className="num">1</span> Bipar NF
                </Step>
                <StepArrow>→</StepArrow>
                <Step $active={reModal.step === 2} $done={false}>
                  <span className="num">2</span> Bipar box
                </Step>
              </StepIndicator>

              {reModal.step === 1 && (
                <>
                  <ReInput
                    ref={reNfInputRef}
                    value={reModal.scanNf}
                    onChange={e => {
                      const v = e.target.value
                      setReModal(m => ({ ...m, scanNf: v, feedback: { kind: null, message: '' } }))
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); reStep1Submit() } }}
                    placeholder="Bipe a NF do pedido"
                    disabled={reModal.busy}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    $kind={reModal.feedback.kind}
                    style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                  />
                  {reModal.feedback.message && (
                    <div style={{
                      marginTop: 10, fontSize: 12,
                      color: reModal.feedback.kind === 'err' ? '#ef4444'
                        : reModal.feedback.kind === 'ok' ? '#00e5a0'
                        : '#8a95a3',
                    }}>
                      {reModal.feedback.message}
                    </div>
                  )}
                </>
              )}

              {reModal.step === 2 && (
                <>
                  <InfoLine>
                    <span>NF <strong>{reModal.order?.order_number}</strong></span>
                    <span>Volume <strong>V{reModal.volumeNumber}</strong></span>
                    {reModal.order?.driver_name && (
                      <span>Motorista <strong>{reModal.order.driver_name}</strong></span>
                    )}
                  </InfoLine>

                  {reModal.suggestion?.box ? (
                    <SuggestionBox>
                      <span className="icon">📦</span>
                      <div className="info">
                        <div className="lbl">Box sugerido</div>
                        <div className="name">{reModal.suggestion.box.name}</div>
                        <div className="meta">
                          {reModal.suggestion.reason === 'already_in_use'
                            ? `Pedido já está neste box (volumes: ${(reModal.suggestion.previous_volumes || []).join(', ') || '—'})`
                            : `Próximo box livre na sequência (#${reModal.suggestion.box.sequence})`}
                        </div>
                      </div>
                    </SuggestionBox>
                  ) : (
                    <InfoLine>
                      <span style={{ color: '#f59e0b' }}>⚠ Nenhum box sugerido — bipe qualquer box ativo</span>
                    </InfoLine>
                  )}

                  <ReInput
                    ref={reBoxInputRef}
                    value={reModal.scanBox}
                    onChange={e => {
                      const v = e.target.value
                      setReModal(m => ({ ...m, scanBox: v, feedback: { kind: null, message: '' } }))
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); reStep2Submit() } }}
                    placeholder={reModal.suggestion?.box?.barcode || 'Bipe o código do box'}
                    disabled={reModal.busy}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    $kind={reModal.feedback.kind}
                    style={isCoarse ? HIDDEN_INPUT_STYLE : { marginTop: 12 }}
                  />
                  {reModal.feedback.message && (
                    <div style={{
                      marginTop: 10, fontSize: 12,
                      color: reModal.feedback.kind === 'err' ? '#ef4444'
                        : reModal.feedback.kind === 'ok' ? '#00e5a0'
                        : '#8a95a3',
                    }}>
                      {reModal.feedback.message}
                    </div>
                  )}
                </>
              )}

              <ModalActions>
                <ModalBtn onClick={closeReEmbark} disabled={reModal.busy}>
                  {reModal.step === 2 ? 'Cancelar' : 'Fechar'}
                </ModalBtn>
                {reModal.step === 1 && (
                  <ModalBtn $primary onClick={reStep1Submit}
                    disabled={reModal.busy || !reModal.scanNf.trim()}>
                    {reModal.busy ? 'Processando…' : 'Continuar →'}
                  </ModalBtn>
                )}
                {reModal.step === 2 && (
                  <ModalBtn $primary onClick={reStep2Submit}
                    disabled={reModal.busy || !reModal.scanBox.trim()}>
                    {reModal.busy ? 'Registrando…' : 'Confirmar bipe'}
                  </ModalBtn>
                )}
              </ModalActions>
            </ReBox>
          </ModalOverlay>

          {/* MODAL — feedback genérico (success / warning / error) */}
          <FeedbackModal
            visible={fbModal.open}
            kind={fbModal.kind}
            title={fbModal.title}
            message={fbModal.message}
            onClose={closeFbModal}
          >
            {/* Conclusão de box: oferece imprimir o romaneio do motorista */}
            {fbModal.print && (
              <FeedbackExtra>
                <div className="lbl">Romaneio do motorista</div>
                <div className="val">🚛 {fbModal.print.driver_name || 'Sem motorista'}</div>
                <button
                  type="button"
                  onClick={handlePrintRomaneio}
                  style={{
                    marginTop: 12,
                    width: '100%',
                    padding: '10px 16px',
                    background: '#00e5a0',
                    border: 'none',
                    borderRadius: 8,
                    color: '#000',
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: "'DM Sans',sans-serif",
                    cursor: 'pointer',
                  }}
                >
                  🖨 Imprimir Romaneio
                </button>
              </FeedbackExtra>
            )}

            {fbModal.correct_box && (
              <FeedbackExtra>
                <div className="lbl">Box correto</div>
                <div className="val">📦 {fbModal.correct_box.name}</div>
                {fbModal.correct_box.barcode && <div className="meta">código {fbModal.correct_box.barcode}</div>}
              </FeedbackExtra>
            )}

            {/* Individual: pedido com volumes pendentes (POST /volume/embark 422) */}
            {fbModal.pending_siblings_count != null && (
              <FeedbackExtra>
                <div className="lbl">Volumes pendentes de coleta</div>
                <div className="val">⚠ {fbModal.pending_siblings_count}</div>
                <div className="meta">Conclua a separação do pedido antes de tentar embarcar.</div>
              </FeedbackExtra>
            )}

            {/* Massa: lista dos pedidos pulados por estarem incompletos */}
            {Array.isArray(fbModal.incomplete_orders) && fbModal.incomplete_orders.length > 0 && (
              <FeedbackExtra>
                <div className="lbl">Pedidos não embarcados</div>
                <div style={{
                  marginTop: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}>
                  {fbModal.incomplete_orders.map(o => (
                    <div
                      key={o.order_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderRadius: 6,
                        background: 'rgba(245, 158, 11, 0.10)',
                        border: '1px solid rgba(245, 158, 11, 0.4)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                        NF {o.order_number || '—'}
                      </span>
                      <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                        ⚠ {o.pending_count} pendente{o.pending_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="meta" style={{ marginTop: 8 }}>
                  Conclua a separação desses pedidos antes de embarcá-los.
                </div>
              </FeedbackExtra>
            )}
          </FeedbackModal>
        </>
      )}
    </Dashboard>
  )
}
